import type { CalendarEvent } from '@/modules/calendar/types';
import type { LogisticsOrder } from '@/modules/logistics/types';
import { LOGISTICS_ORDER_TYPE_LABELS } from '@/modules/logistics/types';
import type { MaintenanceTask } from '@/modules/maintenance/types/maintenance';
import { MAINTENANCE_TYPE_LABELS } from '@/modules/maintenance/types/maintenance';
import type { Reservation } from '@/modules/reservations/types';

export function reservationToCalendarEvent(
  reservation: Reservation,
): CalendarEvent {
  return {
    id: `res-${reservation.id}`,
    source: 'reservation',
    title: reservation.title,
    start: reservation.startDate,
    end: reservation.endDate,
    reservationStatus: reservation.status,
    relatedInventoryItems: reservation.inventoryItemIds,
    clientName: reservation.clientName,
    reservationId: reservation.id,
  };
}

export function logisticsOrderToCalendarEvent(
  order: LogisticsOrder,
): CalendarEvent {
  const typeLabel = LOGISTICS_ORDER_TYPE_LABELS[order.type];
  const event: CalendarEvent = {
    id: `log-${order.id}`,
    source: 'logistics',
    title: `${typeLabel}${order.contactName ? ` · ${order.contactName}` : ''}`,
    start: order.scheduledDate,
    end: order.scheduledDate,
    logisticsStatus: order.status,
    logisticsType: order.type,
    relatedInventoryItems: order.inventoryItemIds,
    clientName: order.contactName,
    logisticsOrderId: order.id,
  };
  if (order.reservationId) {
    event.reservationId = order.reservationId;
  }
  return event;
}

export function reservationsToCalendarEvents(
  reservations: Reservation[],
): CalendarEvent[] {
  return reservations.map(reservationToCalendarEvent);
}

export function logisticsOrdersToCalendarEvents(
  orders: LogisticsOrder[],
): CalendarEvent[] {
  return orders
    .filter((o) => o.status !== 'cancelled')
    .map(logisticsOrderToCalendarEvent);
}

export function maintenanceTaskToCalendarEvent(
  task: MaintenanceTask,
): CalendarEvent {
  const typeLabel = MAINTENANCE_TYPE_LABELS[task.type];
  return {
    id: `mnt-${task.id}`,
    source: 'maintenance',
    title: `${typeLabel}: ${task.title}`,
    start: task.scheduledStart,
    end: task.scheduledEnd,
    relatedInventoryItems: [task.inventoryItemId],
    clientName: task.assignedTo || '—',
    maintenanceTaskId: task.id,
    maintenanceStatus: task.status,
    maintenanceType: task.type,
  };
}

export function maintenanceTasksToCalendarEvents(
  tasks: MaintenanceTask[],
): CalendarEvent[] {
  return tasks
    .filter((t) => t.status !== 'cancelled')
    .map(maintenanceTaskToCalendarEvent);
}
